// 「发起签名」— 打通签字确认插件（feishu-sign, sign.xiaomiao.win）。
//
// 排版打印负责把文档渲染成图片；签署链接、H5 手写签名、配额计费、
// 签署记录管理全部复用签字确认插件的现有服务，本插件零后端。
// 账号体系天然互通：两边的 accountId 都是 Base SDK 的 baseUserId。

const SIGN_API_BASE = 'https://sign.xiaomiao.win';

/** 签字确认插件的市场安装链接（引导用户去管理签署进度/充值）。 */
export const SIGN_PLUGIN_NAME = '签字确认';

export interface SignQuota {
  remaining: number;
}

export interface CreateSignResult {
  sheetId: string;
  records: Array<{ recordId: string; confirmId: string; token: string; shareUrl: string }>;
  remainingQuota: number;
}

export class SignQuotaError extends Error {
  remaining: number;
  constructor(message: string, remaining: number) {
    super(message);
    this.remaining = remaining;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${SIGN_API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (res.status === 402) {
    throw new SignQuotaError(data?.message || '剩余签字次数不足', Number(data?.remaining ?? 0));
  }
  if (!res.ok) throw new Error((data && data.message) || `HTTP ${res.status}`);
  return data as T;
}

export const signApi = {
  quota(accountId: string): Promise<SignQuota> {
    return req(`/api/billing/quota?accountId=${encodeURIComponent(accountId)}`);
  },

  /** 把文档图片转存到签字服务的 R2（飞书临时 URL 会过期，H5 需要持久 URL）。 */
  async uploadImage(dataUrl: string): Promise<string> {
    const r = await req<{ url: string }>('/api/media', {
      method: 'POST',
      body: JSON.stringify({ imageBase64: dataUrl }),
    });
    return r.url;
  },

  /** 为当前文档创建一条签署链接（消耗签字确认插件的配额，免费 30 次）。
   *  signAreas 非空 → H5 走文档叠加签署（签名烤进文档指定位置）。 */
  createSignLink(input: {
    baseId: string;
    tableId: string;
    creatorId: string;
    docName: string;
    recordId: string;
    imageUrls: string[];
    signAreas?: SignAreaPct[];
  }): Promise<CreateSignResult> {
    return req('/api/sheets', {
      method: 'POST',
      body: JSON.stringify({
        baseId: input.baseId,
        tableId: input.tableId,
        creatorId: input.creatorId,
        name: input.docName,
        type: 'content_sign',
        signMode: 'single',
        verifyEnabled: false,
        autoSync: false,
        saveAsImage: true,
        // 内容 = 排版打印渲染好的整页文档图（contentFields 仅为满足
        // 服务端校验的占位描述；真正展示的是 content 快照里的 images）
        contentFields: [{ id: 'doc', name: '签署文件', type: 1, order: 0 }],
        ...(input.signAreas && input.signAreas.length > 0 ? { signAreas: input.signAreas } : {}),
        records: [
          {
            recordId: input.recordId,
            content: [{ name: '签署文件', value: '', images: input.imageUrls }],
          },
        ],
      }),
    });
  },
};

/** 签名区域（相对文档图的百分比），与 sign 插件 shared 的 SignArea 一致。 */
export interface SignAreaPct {
  id: string;
  label: string;
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
}
